import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    AlertTitle,
    Button,
    Chip,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlugCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';

import { useApplicationContext } from 'ApplicationContext';
import SettingsPageHeader from './SettingsPageHeader';
import { settingsApi } from './settingsApi';

/** Formularzustand — der Token läuft getrennt, weil er der Drei-Zustands-Regel folgt. */
interface YouTrackForm {
    baseUrl: string;
    projectShortName: string;
    feedbackFormBaseUrl: string;
    feedbackFormId: string;
}

/*
 * Lokal statt aus dem erzeugten Client: `src/apiClient/Api.ts` entsteht aus dem Swagger eines
 * LAUFENDEN Servers (`npm run generate-api`) und kennt die YouTrack-Endpunkte noch nicht.
 */
interface YouTrackSettingsDto extends YouTrackForm {
    accessTokenConfigured: boolean;
    isComplete: boolean;
    accessTokenUnreadable: boolean;
}

interface YouTrackTestResult {
    success: boolean;
    message: string;
    elapsedMilliseconds: number;
    login: string | null;
}

const emptyForm: YouTrackForm = {
    baseUrl: '',
    projectShortName: '',
    feedbackFormBaseUrl: '',
    feedbackFormId: '',
};

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Einstellungen der YouTrack-Anbindung: Zugriffstoken, Basis-URL, Helpdesk-Projekt und das
 * Feedback-Formular, über das Tickets ohne YouTrack-Konto entstehen.
 *
 * Die Werte greifen ohne Server-Neustart: sie liegen in `ServerSettings` und überlagern die
 * Startwerte aus `appsettings.json`. Ein leeres Feld heißt „zurück auf die Vorgabe" — nur der
 * Token folgt der Drei-Zustands-Regel (unverändert / löschen / neu setzen).
 *
 * Deutsch fest verdrahtet — wie die Geschwisterseiten (Microsoft 365, KI-Assistent).
 */
export const YouTrackSettings: React.FC = () => {
    const context = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();
    // Über request() statt api.instance — sonst fehlt der Authorization-Header.
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [form, setForm] = useState<YouTrackForm>(emptyForm);
    const [saved, setSaved] = useState<YouTrackForm>(emptyForm);
    const [hasToken, setHasToken] = useState(false);
    const [tokenUnreadable, setTokenUnreadable] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    // null = unverändert lassen; '' = löschen; sonst neu setzen. Spiegelt die Serverregel.
    const [accessToken, setAccessToken] = useState<string | null>(null);

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState<YouTrackTestResult | null>(null);

    const patch = (changes: Partial<YouTrackForm>) =>
        setForm((prev) => ({ ...prev, ...changes }));

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await http.get<YouTrackSettingsDto>('/YouTrackSettings/Settings');
            const { accessTokenConfigured, isComplete: complete, accessTokenUnreadable, ...rest } = r.data;
            const next = { ...emptyForm, ...rest };
            setForm(next);
            setSaved(next);
            setHasToken(accessTokenConfigured);
            setTokenUnreadable(accessTokenUnreadable);
            setIsComplete(complete);
            setAccessToken(null);
        } catch (e) {
            context.apiError(e);
        } finally {
            setLoading(false);
        }
    }, [context, http]);

    useEffect(() => {
        void load();
    }, [load]);

    /** Ungespeicherte Änderungen — auch ein neu getippter Token zählt. */
    const dirty = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(saved) || accessToken !== null,
        [form, saved, accessToken],
    );

    /**
     * Was fehlt, damit die Einstellungen tragen. Wird vor dem Speichern angezeigt statt danach —
     * ein Serverfehler auf eine Eingabe, die man schon beim Tippen sieht, ist ärgerlich.
     */
    const problems = useMemo(() => {
        const out: string[] = [];
        const url = form.baseUrl.trim();
        const formUrl = form.feedbackFormBaseUrl.trim();
        const formId = form.feedbackFormId.trim();

        if (url && !/^https?:\/\//i.test(url)) {
            out.push('Die Basis-URL muss mit http:// oder https:// beginnen.');
        }
        if (formUrl && !/^https?:\/\//i.test(formUrl)) {
            out.push('Die Formular-Basis-URL muss mit http:// oder https:// beginnen.');
        }
        if (formId && !GUID_PATTERN.test(formId)) {
            out.push('Die Formular-UUID muss eine GUID sein — sie steht in der Adresszeile des Formulars in YouTrack.');
        }
        return out;
    }, [form]);

    const save = async (): Promise<void> => {
        setBusy(true);
        try {
            await http.put('/YouTrackSettings/Settings', { ...form, accessToken });
            enqueueSnackbar('Gespeichert — greift ohne Neustart.', { variant: 'success' });
            setTest(null);
            await load();
        } catch (e) {
            context.apiError(e);
        } finally {
            setBusy(false);
        }
    };

    /**
     * Der Test läuft immer gegen den **gespeicherten** Stand — ungespeicherte Feldänderungen
     * sieht er nicht. Deshalb warnt der Knopf, solange das Formular vom Server abweicht.
     */
    const runTest = async (): Promise<void> => {
        setTesting(true);
        setTest(null);
        try {
            const r = await http.post<YouTrackTestResult>('/YouTrackSettings/TestConnection');
            setTest(r.data);
        } catch (e) {
            context.apiError(e);
        } finally {
            setTesting(false);
        }
    };

    const statusChip = loading
        ? null
        : isComplete
            ? <Chip size="small" color="success" label="einsatzbereit"/>
            : <Chip size="small" color="warning" label="unvollständig konfiguriert"/>;

    return (
        <Grid size={12}>
            <SettingsPageHeader sectionKey="youtrack" chip={statusChip}/>
            {loading && <LinearProgress sx={{ mb: 2 }}/>}

            <Grid container spacing={2}>
                {tokenUnreadable && (
                    <Grid size={12}>
                        <Alert severity="warning">
                            Es liegt ein gespeicherter Zugriffstoken vor, der sich nicht entschlüsseln lässt —
                            typisch nach neu erzeugten DataProtection-Keys. Bitte neu eintragen.
                        </Alert>
                    </Grid>
                )}

                <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Verbindung</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Der Server arbeitet in YouTrack unter dem Konto, das den Token erzeugt hat —
                                es liest die Ticketliste, legt Kommentare an und lädt Ticket-Details. Einen
                                permanenten Token erzeugt man in YouTrack unter{' '}
                                <strong>Profil → Kontosicherheit → Neuer Token</strong> (beginnt mit{' '}
                                <code>perm:</code>).
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <TextField fullWidth size="small" label="Basis-URL"
                                               value={form.baseUrl}
                                               onChange={(e) => patch({ baseUrl: e.target.value })}
                                               placeholder="http://192.168.1.25:8080"
                                               helperText="Adresse der YouTrack-Instanz für alle API-Aufrufe."/>
                                </Grid>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                                        <TextField
                                            fullWidth size="small" type="password" label="Permanenter Zugriffstoken"
                                            value={accessToken ?? ''}
                                            disabled={accessToken === null && hasToken}
                                            onChange={(e) => setAccessToken(e.target.value)}
                                            placeholder={hasToken ? '•••••••• (gespeichert)' : 'perm:…'}
                                            helperText={accessToken === null && hasToken
                                                ? 'Hinterlegt. Zum Ersetzen auf „Ändern“ klicken — der gespeicherte Wert wird nie ausgeliefert.'
                                                : 'Wird verschlüsselt gespeichert (DataProtection) und nie wieder angezeigt.'}/>
                                        {hasToken && (
                                            <Button size="small" sx={{ mt: 0.5 }}
                                                    onClick={() => setAccessToken(accessToken === null ? '' : null)}>
                                                {accessToken === null ? 'Ändern' : 'Abbrechen'}
                                            </Button>
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Helpdesk-Projekt und Feedback-Formular
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Tickets aus dem Support-System entstehen über ein YouTrack-Feedback-Formular —
                                so braucht der Kunde kein YouTrack-Konto. Die Formular-Basis-URL kann von der
                                API-Basis-URL abweichen, wenn YouTrack unter mehreren Namen erreichbar ist.
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField fullWidth size="small" label="Projekt (Kurzname)"
                                               value={form.projectShortName}
                                               onChange={(e) => patch({ projectShortName: e.target.value })}
                                               placeholder="SUPPORT_HELPDESK"
                                               helperText="Kurzname des Helpdesk-Projekts in YouTrack."/>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField fullWidth size="small" label="Formular-Basis-URL"
                                               value={form.feedbackFormBaseUrl}
                                               onChange={(e) => patch({ feedbackFormBaseUrl: e.target.value })}
                                               placeholder="http://youtrack.reichert-gmbh.local:8080"
                                               helperText="Host für die Formular-Aufrufe."/>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <TextField fullWidth size="small" label="Formular-UUID"
                                               value={form.feedbackFormId}
                                               onChange={(e) => patch({ feedbackFormId: e.target.value })}
                                               placeholder="430247ff-…"
                                               helperText="UUID des Feedback-Formulars (aus dessen Adresse in YouTrack)."/>
                                </Grid>
                            </Grid>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            {problems.length > 0 && (
                                <Alert severity="warning">
                                    <AlertTitle>Bitte prüfen</AlertTitle>
                                    {problems.map((problem) => <div key={problem}>{problem}</div>)}
                                </Alert>
                            )}

                            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                                <Button variant="contained" onClick={save}
                                        disabled={busy || loading || problems.length > 0}>
                                    {busy ? 'Speichere …' : 'Speichern'}
                                </Button>
                                <Tooltip title={dirty ? 'Das Formular hat ungespeicherte Änderungen — der Test prüft den gespeicherten Stand.' : ''}>
                                    <span>
                                        <Button variant="outlined" onClick={runTest}
                                                disabled={testing || loading}
                                                color={dirty ? 'warning' : 'primary'}
                                                startIcon={<FontAwesomeIcon icon={faPlugCircleCheck}/>}>
                                            {testing ? 'Teste …' : 'Verbindung testen'}
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Button variant="text" onClick={load} disabled={busy || loading}>
                                    Verwerfen
                                </Button>
                            </Stack>

                            {test && (
                                <Alert severity={test.success ? 'success' : 'error'}>
                                    <AlertTitle>{test.success ? 'Test erfolgreich' : 'Test fehlgeschlagen'}</AlertTitle>
                                    {test.message}
                                    <Typography variant="caption" color="text.secondary"
                                                sx={{ display: 'block', mt: 1 }}>
                                        Dauer: {test.elapsedMilliseconds} ms
                                        {test.login && ` · Konto: ${test.login}`}
                                    </Typography>
                                </Alert>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default YouTrackSettings;
